import React, { forwardRef } from "react";
import { constants } from "@/constants.js";
import { urlChatID } from "@/utils/url.js";
import { toast } from "react-toastify";

import { useQueryClient, useQuery, useMutation } from "react-query";
import Icon from "@/components/ui/Icon.jsx";
import { apiParserStatus, apiParserStop } from "@/store/parser.js";
import { EventBus } from "@/store/event-bus.ts";
import { queryKeyChatbotSummary } from "@/pages/chatbot/sources/ChatBotSources.jsx";
import DocStatusBadge from "@/components/DocStatusBadge.jsx";
import { queryKey } from "@/store/queryKeys.ts";
import Loading from "@/components/Loading";
import {useTranslation} from "react-i18next";

let checkStatusTimer = null;
let prevProgress = null;

const ListLinks = () => {
  const {t} = useTranslation();
  const queryClient = useQueryClient();
  let { isLoading, isError, error, data, refetch } = useQuery(
    queryKey.chatbotSourcesLinks(),
    apiParserStatus,
    {
      staleTime: 180000,
    },
  );

  // const [parsing, setParsing] = React.useState(false)
  const [clickHandler, setclickHandler] = React.useState(false);

  const refetcher = async function () {
    return refetch().then((res) => {
      if (res.data) {
        startWatcher();
        if (prevProgress === true && !res.data.progress) {
          toast.success("Site crawling has finished", {
            position: "top-right",
            autoClose: 1500,
            theme: "light",
          });
        }
        !res.data.progress && clearInterval(checkStatusTimer);

        prevProgress = res.data.progress;
        queryClient.invalidateQueries(queryKeyChatbotSummary);
      }
    });
  };
  const startWatcher = (force) => {
    if (force || (data && data.progress && !checkStatusTimer)) {
      checkStatusTimer = setInterval(refetcher, 4000);
    }
  };
  startWatcher();

  if (!clickHandler) {
    setclickHandler(true);
    EventBus.getInstance().registerSingle("parser.start", async function () {
      await refetcher();
      startWatcher(true);
    });
  }
  const apiDocumentDelete = (id, nested) => {
    return fetch(
      `${constants.url.API_PREFIX}chatbot/${urlChatID()}/document/${id}/delete${
        nested ? "?nested=true" : ""
      }`,
    ).then(() => {
      queryClient.invalidateQueries(queryKey.chatbotSourcesLinks());
      queryClient.invalidateQueries(queryKeyChatbotSummary);
    });
  };

  if (isLoading) return <Loading />;
  if (isError || !data)
    return <p>Error: Can't load links for chatbot training</p>;

  let charactersTotal = 0;
  return (
    <div className="mt-4">
      {data.progress && (
        <button
          type="button"
          className="btn justify-center btn-outline-dark btn-sm flex items-center mb-4"
          onClick={async function () {
            await apiParserStop();
            queryClient.invalidateQueries(queryKey.chatbotSourcesLinks());
          }}
        >
          <svg
            className="animate-spin ltr:-ml-1 ltr:mr-3 rtl:-mr-1 rtl:ml-3 h-5 w-5 unset-classname"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          Stop crawling
        </button>
      )}

      <div className="py-1 font-bold flex justify-between">
        <div className="w-1/2 md:w-3/4 flex items-center">
          {t("knowledge_base.web_pages.links")}{" "}
          {data.links_total ? `(${data.links_total})` : ``}
        </div>
        <div className="flex justify-center w-[8%]">
          {t("knowledge_base.web_pages.nested")}
        </div>
        <div className="flex justify-center w-[8%]">{t("knowledge_base.characters")}</div>
        <div className="flex justify-center w-[8%]">{t("knowledge_base.actions")}</div>
      </div>
      {data.links.map((link, i) => {
        charactersTotal += link.characters ? link.characters : 0;
        return (
          <div key={i} className="py-1 hover:bg-slate-200 flex justify-between">
            <div className="w-1/2 md:w-3/4 flex">
              <div className="w-20">
                <DocStatusBadge status={link.status} />
              </div>
              <a
                href={link.url}
                target="_blank"
                className="ml-4"
                style={{
                  paddingLeft: `${link.depth * 2}%`,
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {link.norm_url}
                {link.pseudo && (
                  <span className="ml-2 text-slate-400">(group of URLs)</span>
                )}
              </a>
            </div>
            <div className="flex justify-center w-[8%]">
              {!!link.children && <span>{link.children}</span>}
            </div>
            <div className="flex justify-center w-[8%]">
              {!link.pseudo && !!link.characters && link.characters}
            </div>
            <div className="flex justify-center items-center w-[8%]">
              <div
                className="cursor-pointer"
                title={"Delete link"}
                onClick={
                  link.pseudo ? undefined : (e) => apiDocumentDelete(link.id)
                }
              >
                <Icon
                  icon="heroicons:archive-box-x-mark"
                  className={
                    "w-6 h-6 " +
                    (!link.pseudo
                      ? "text-gray-400 hover:text-red-600"
                      : "text-gray-300")
                  }
                />
              </div>
              {!!link.children && (
                <div
                  className="cursor-pointer"
                  title={`Delete ${!link.pseudo ? "with" : ""} nested links`}
                  onClick={(e) => apiDocumentDelete(link.id, true)}
                >
                  <Icon
                    icon="heroicons:archive-box-arrow-down"
                    className="w-6 h-6 text-gray-400 hover:text-red-600"
                  />
                </div>
              )}
            </div>
          </div>
        );
      })}
      {!!data.links_total && (
        <div className="py-1 font-bold flex justify-between">
          <div className="w-3/4 flex items-center">
            Links total: {data.links_total}
          </div>
          <div className="flex justify-center w-20"></div>
          <div className="flex justify-center w-20">{charactersTotal}</div>
          <div className="flex justify-center w-20"></div>
        </div>
      )}
    </div>
  );
};

export default ListLinks;
